import { useState, useEffect, useRef, useCallback } from "react";
import { saveLayoutContent, createLayout } from "wasp/client/operations";
import { useToast } from "../../client/hooks/use-toast";
import useTranslation from "../../client/hooks/useTranslation";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "./types";
import { GRID_COLS } from "./types";
import { clampColSpan, clampRowSpan, WIDGET_REGISTRY } from "./registry";

const DEBOUNCE_MS = 500;

export function compactSlots(slots: SlotPlacement[]): SlotPlacement[] {
  const topbarSlots = slots.filter((s) => s.placement === "topbar");
  const gridSlots = slots.filter((s) => s.placement !== "topbar");
  if (gridSlots.length === 0) return topbarSlots;

  // 2D gravity packing. Widgets are placed in reading order (top→bottom, then
  // left→right). Each widget falls into the highest free position; at equal
  // height it slides as far left as possible. This fills BOTH vertical gaps
  // (widget rowSpan shorter than the space above) AND horizontal gaps (an empty
  // column next to a narrower widget) — a vertical-only pack left those holes.
  const sorted = [...gridSlots].sort((a, b) => a.rowStart - b.rowStart || a.colStart - b.colStart);
  const placed: SlotPlacement[] = [];

  const fits = (colStart: number, rowStart: number, slot: SlotPlacement): boolean => {
    if (colStart < 1 || colStart + slot.colSpan - 1 > GRID_COLS) return false;
    return !placed.some((p) => {
      const colOverlap = colStart < p.colStart + p.colSpan && colStart + slot.colSpan > p.colStart;
      const rowOverlap = rowStart < p.rowStart + p.rowSpan && rowStart + slot.rowSpan > p.rowStart;
      return colOverlap && rowOverlap;
    });
  };

  for (const slot of sorted) {
    const maxColStart = GRID_COLS - slot.colSpan + 1;
    // Scan row by row from the top; within a row, take the leftmost free column.
    // The first hit is the highest-then-leftmost valid spot for this widget.
    let best: { col: number; row: number } | null = null;
    for (let row = 1; !best; row++) {
      for (let col = 1; col <= maxColStart; col++) {
        if (fits(col, row, slot)) { best = { col, row }; break; }
      }
    }
    placed.push({ ...slot, colStart: best.col, rowStart: best.row });
  }

  return [...topbarSlots, ...placed];
}

function hasOverlap(slots: SlotPlacement[], candidate: SlotPlacement): boolean {
  for (const s of slots) {
    if (s.id === candidate.id) continue;
    const colOverlap = candidate.colStart < s.colStart + s.colSpan && candidate.colStart + candidate.colSpan > s.colStart;
    const rowOverlap = candidate.rowStart < s.rowStart + s.rowSpan && candidate.rowStart + candidate.rowSpan > s.rowStart;
    if (colOverlap && rowOverlap) return true;
  }
  return false;
}

function uid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function useActiveLayout(
  initial: LayoutSnapshot,
  activeLayoutId: string | null,
  onLayoutEnsured?: (newLayoutId: string) => void,
  isReadOnly?: boolean,
) {
  const t = useTranslation();
  const { toast } = useToast();
  const [layout, setLayout] = useState<LayoutSnapshot>(initial);

  const layoutRef = useRef<LayoutSnapshot>(layout);
  const layoutIdRef = useRef<string | null>(activeLayoutId);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLayoutEnsuredRef = useRef(onLayoutEnsured);
  // Track the layout ID we last initialized from server — only reset local state
  // when the user switches to a *different* layout, never on re-fetches of the same one.
  const initializedForLayoutIdRef = useRef<string | null>(activeLayoutId);
  const isSavingRef = useRef(false);
  const isDirtyRef = useRef(false);      // true when local state has unsaved edits
  const everEditedRef = useRef(false);   // true once the user has made any change this session

  useEffect(() => { layoutRef.current = layout; }, [layout]);
  useEffect(() => { onLayoutEnsuredRef.current = onLayoutEnsured; }, [onLayoutEnsured]);

  // Only reset local state when the active layout truly switches to a different one.
  // Ignore re-renders caused by server refetches of the same layout (those would wipe edits).
  useEffect(() => {
    if (activeLayoutId === initializedForLayoutIdRef.current) return;
    // Different layout selected — flush any pending save for the old one, then switch.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      const oldSnapshot = layoutRef.current;
      const oldId = layoutIdRef.current;
      if (oldId && !isReadOnly) {
        (saveLayoutContent as any)({
          layoutId: oldId,
          rows: serializeSlotsToRows(oldSnapshot.slots),
        }).catch(() => {});
      }
    }
    initializedForLayoutIdRef.current = activeLayoutId;
    layoutIdRef.current = activeLayoutId;
    isDirtyRef.current = false;
    everEditedRef.current = false;
    setLayout(initial);
  }, [activeLayoutId]); // intentionally NOT including `initial` — we only react to layout ID changes

  // Sync from server only if the user has never touched this layout in this session.
  // Once they've edited anything, the local state is authoritative until they switch layouts.
  useEffect(() => {
    if (!everEditedRef.current) {
      layoutRef.current = initial;
      setLayout(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  // Flush any pending save on unmount (user navigates away without clicking "Terminé").
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (!isReadOnly && isDirtyRef.current && layoutIdRef.current) {
        (saveLayoutContent as any)({
          layoutId: layoutIdRef.current,
          rows: serializeSlotsToRows(layoutRef.current.slots),
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistSnapshot = useCallback(async (snapshot: LayoutSnapshot, targetLayoutId: string | null) => {
    if (isReadOnly) return;
    isSavingRef.current = true;
    let id = targetLayoutId;
    try {
      if (!id) {
        const defaultName = (t as any)?.dashboard?.layouts?.newLayout ?? "Mon layout par défaut";
        const created = await (createLayout as any)({ name: defaultName });
        id = (created as any).id as string;
        layoutIdRef.current = id;
        if (onLayoutEnsuredRef.current && id) onLayoutEnsuredRef.current(id);
      }
      const rows = serializeSlotsToRows(snapshot.slots);
      const assignments: { slotId: string; notebookNoteId: string }[] =
        await (saveLayoutContent as any)({ layoutId: id, rows }) ?? [];
      isDirtyRef.current = false;
      // Patch notebookNoteId into local slots for any newly-created note widgets
      if (assignments.length > 0) {
        setLayout((prev) => {
          const map = new Map(assignments.map((a) => [a.slotId, a.notebookNoteId]));
          const patched = prev.slots.map((s) => {
            const nid = map.get(s.id);
            return nid ? { ...s, notebookNoteId: nid } : s;
          });
          const next = { ...prev, slots: patched };
          layoutRef.current = next;
          return next;
        });
      }
      toast({ description: t.dashboard.widgetSaved, duration: 1500 });
    } catch (err: any) {
      // 403 means readonly or not-owned layout — skip silently (no error toast)
      if (err?.statusCode === 403 || err?.status === 403) {
        console.warn("[useActiveLayout] save skipped 403 for layoutId:", id, err?.message);
      } else {
        console.error("[useActiveLayout] save FAILED:", err);
        toast({ description: t.dashboard.widgetSaveError, variant: "destructive" });
      }
    } finally {
      isSavingRef.current = false;
    }
  }, [t, toast]);

  const scheduleSave = useCallback((next: LayoutSnapshot) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    isSavingRef.current = true;
    const idAtScheduleTime = layoutIdRef.current;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      persistSnapshot(next, idAtScheduleTime);
    }, DEBOUNCE_MS);
  }, [persistSnapshot]);

  const flushSave = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    await persistSnapshot(layoutRef.current, layoutIdRef.current);
  }, [persistSnapshot]);

  const compactAndFlushSave = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const compacted = { ...layoutRef.current, slots: compactSlots(layoutRef.current.slots) };
    layoutRef.current = compacted;
    setLayout(compacted);
    await persistSnapshot(compacted, layoutIdRef.current);
  }, [persistSnapshot]);

  const updateLayout = useCallback((updater: (prev: LayoutSnapshot) => LayoutSnapshot) => {
    setLayout((prev) => {
      const next = updater(prev);
      if (next === prev) return prev; // no change, skip save
      layoutRef.current = next;
      isDirtyRef.current = true;
      everEditedRef.current = true;
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const addWidget = useCallback((
    widgetKey: WidgetKey,
    colStart: number,
    rowStart: number,
    requestedColSpan?: number,
    requestedRowSpan?: number,
    placement?: "grid" | "topbar",
    defaultFilterValue?: string | null,
  ) => {
    updateLayout((prev) => {
      if (placement === "topbar") {
        // Prevent duplicate topbar widget keys
        if (prev.slots.some((s) => s.placement === "topbar" && s.widgetKey === widgetKey)) return prev;
        const newSlot: SlotPlacement = {
          id: uid(),
          widgetKey,
          colStart: 1,
          rowStart: 0,
          colSpan: 1,
          rowSpan: 1,
          placement: "topbar",
          ...(defaultFilterValue !== undefined ? { defaultFilterValue } : {}),
        };
        return { slots: [...prev.slots, newSlot] };
      }
      const reg = WIDGET_REGISTRY[widgetKey];
      const available = GRID_COLS - colStart + 1;
      const colSpan = clampColSpan(widgetKey, requestedColSpan ?? reg.defaultColSpan, available);
      const rowSpan = clampRowSpan(widgetKey, requestedRowSpan ?? reg.defaultRowSpan);
      let newSlot: SlotPlacement = { id: uid(), widgetKey, colStart, rowStart, colSpan, rowSpan };
      if (hasOverlap(prev.slots, newSlot)) {
        // Try shrinking to minRowSpan, then minColSpan × minRowSpan
        const minRow = clampRowSpan(widgetKey, reg.minRowSpan);
        const candidateMinRow = { ...newSlot, rowSpan: minRow };
        if (!hasOverlap(prev.slots, candidateMinRow)) {
          newSlot = candidateMinRow;
        } else {
          const minCol = clampColSpan(widgetKey, reg.minColSpan, available);
          const candidateMin = { ...newSlot, colSpan: minCol, rowSpan: minRow };
          if (!hasOverlap(prev.slots, candidateMin)) {
            newSlot = candidateMin;
          } else {
            return prev;
          }
        }
      }
      return { slots: [...prev.slots, newSlot] };
    });
  }, [updateLayout]);

  const removeWidget = useCallback((slotId: string) => {
    updateLayout((prev) => ({ slots: prev.slots.filter((s) => s.id !== slotId) }));
  }, [updateLayout]);

  const setWidgetSize = useCallback((slotId: string, colSpan: number, rowSpan: number) => {
    updateLayout((prev) => {
      const slots = prev.slots.map((s) => {
        if (s.id !== slotId) return s;
        const available = GRID_COLS - s.colStart + 1;
        const candidate = {
          ...s,
          colSpan: clampColSpan(s.widgetKey, colSpan, available),
          rowSpan: clampRowSpan(s.widgetKey, rowSpan),
        };
        return hasOverlap(prev.slots, candidate) ? s : candidate;
      });
      return { slots };
    });
  }, [updateLayout]);

  const moveWidget = useCallback((slotId: string, colStart: number, rowStart: number) => {
    updateLayout((prev) => {
      const slots = prev.slots.map((s) => {
        if (s.id !== slotId) return s;
        const available = GRID_COLS - colStart + 1;
        const candidate = {
          ...s,
          colStart,
          rowStart,
          colSpan: clampColSpan(s.widgetKey, s.colSpan, available),
          rowSpan: clampRowSpan(s.widgetKey, s.rowSpan),
        };
        return hasOverlap(prev.slots, candidate) ? s : candidate;
      });
      return { slots };
    });
  }, [updateLayout]);

  const swapWidgets = useCallback((slotIdA: string, slotIdB: string) => {
    updateLayout((prev) => {
      const a = prev.slots.find((s) => s.id === slotIdA);
      const b = prev.slots.find((s) => s.id === slotIdB);
      if (!a || !b) return prev;
      // Each widget takes the other's full position AND size to avoid overlaps.
      // Clamp to each widget's own min/max constraints at the destination position.
      return {
        slots: prev.slots.map((s) => {
          if (s.id === slotIdA) return {
            ...s,
            colStart: b.colStart, rowStart: b.rowStart,
            colSpan: clampColSpan(s.widgetKey, b.colSpan, GRID_COLS - b.colStart + 1),
            rowSpan: clampRowSpan(s.widgetKey, b.rowSpan),
          };
          if (s.id === slotIdB) return {
            ...s,
            colStart: a.colStart, rowStart: a.rowStart,
            colSpan: clampColSpan(s.widgetKey, a.colSpan, GRID_COLS - a.colStart + 1),
            rowSpan: clampRowSpan(s.widgetKey, a.rowSpan),
          };
          return s;
        }),
      };
    });
  }, [updateLayout]);

  const updateSlotDefaultFilterValue = useCallback((slotId: string, value: string | null) => {
    updateLayout((prev) => ({
      slots: prev.slots.map((s) =>
        s.id === slotId ? { ...s, defaultFilterValue: value } : s
      ),
    }));
  }, [updateLayout]);

  const updateSlotViewMode = useCallback((slotId: string, mode: "chart" | "table") => {
    updateLayout((prev) => ({
      slots: prev.slots.map((s) =>
        s.id === slotId ? { ...s, viewMode: mode } : s
      ),
    }));
  }, [updateLayout]);

  const resetToServerState = useCallback((snapshot: LayoutSnapshot) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    isDirtyRef.current = false;
    everEditedRef.current = false;
    layoutRef.current = snapshot;
    setLayout(snapshot);
  }, []);

  return { layout, addWidget, removeWidget, setWidgetSize, moveWidget, swapWidgets, flushSave, compactAndFlushSave, updateSlotDefaultFilterValue, updateSlotViewMode, resetToServerState };
}

/**
 * Serialize flat slots into the legacy row-based format for the server.
 * Each unique rowStart becomes a virtual row with cols=GRID_COLS.
 * row.order = the global rowStart value (used for deserialization).
 * Topbar slots (placement === "topbar") are placed in a virtual row with order=0.
 */
function serializeSlotsToRows(slots: SlotPlacement[]) {
  // Separate topbar slots from grid slots
  const gridSlots = slots.filter((s) => s.placement !== "topbar");
  const topbarSlots = slots.filter((s) => s.placement === "topbar");

  const rowStarts = [...new Set(gridSlots.map((s) => s.rowStart))].sort((a, b) => a - b);
  const gridRows = rowStarts.map((rowStart) => ({
    order: rowStart,
    cols: GRID_COLS,
    slots: gridSlots
      .filter((s) => s.rowStart === rowStart)
      .map((s) => ({
        id: s.id,
        widgetKey: s.widgetKey,
        colStart: s.colStart,
        rowStart: 1,
        colSpan: s.colSpan,
        rowSpan: s.rowSpan,
        order: s.colStart,
        ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
        ...(s.defaultFilterValue !== undefined ? { defaultFilterValue: s.defaultFilterValue } : {}),
        ...(s.viewMode !== undefined ? { viewMode: s.viewMode } : {}),
      })),
  }));

  if (topbarSlots.length === 0) return gridRows;

  // Topbar slots serialized in row order=0
  const topbarRow = {
    order: 0,
    cols: GRID_COLS,
    slots: topbarSlots.map((s, i) => ({
      id: s.id,
      widgetKey: s.widgetKey,
      colStart: i + 1,
      rowStart: 1,
      colSpan: 1,
      rowSpan: 1,
      order: i,
      placement: "topbar" as const,
      ...(s.defaultFilterValue !== undefined ? { defaultFilterValue: s.defaultFilterValue } : {}),
    })),
  };

  return [topbarRow, ...gridRows];
}
